import React, { useState, useEffect, useCallback } from "react";
import { IEvent, IShow, IBreed } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { helperService } from "src/services/helper.service";
import { AxiosError } from "axios";
import { toast } from "../../../../components/Toast/ToastManager"
import { apiService } from "../../../../services/api.service";

import Input from "src/components/Input/Input";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Checkbox, { ICheck }from "src/components/Checkbox/Checkbox";

import SearchDown from "../../../SearchDown/SearchDown";
import star from "../../../../assets/icons/star.svg";

import { ShowTypes } from "./ShowTypes.definition";

import styles from "./ShowTypeTab.module.scss";

interface IProps {
  event: IEvent,
  show?: IShow,
  onChange(clubName: string, showType: IOption, showElements: ICheck[], elementJudges: ICheck[], armNum: number, breedSearch: string): void
}

const ShowTypeTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [clubName, setClubName] = useState<string>(props.show?.club_name ?? props.event.club?.club_name ?? "");
  const [showType, setShowType] = useState<IOption>({ id: "None", value: "None" });

  const getArmBandNumStart = useCallback((_showType: string) => {
    let hasArmNum = props.event.arm_numbers_config?.filter((a: any) =>  a.show_type === _showType);

    if (hasArmNum && hasArmNum.length > 0) {
      return hasArmNum[0].arm_number_start.toString();
    }

    return "100";
  }, [props.event.arm_numbers_config]);

  const [showTypes, setShowTypes] = useState<IOption[]>([]);
  const [armNum, setArmNum] = useState<string>(getArmBandNumStart(showType.id));
  const [showElements, setShowElements] = useState<ICheck[]>([]);
  const [showElementJudges, setShowElementJudges] = useState<ICheck[]>([]);
  const [selectedBreed, setSelectedBreed] = useState<IBreed>();
  const [breedList, setBreedList] = useState<IBreed[]>([]);
  const [breedSearch, setBreedSearch] = useState<string>("");

  const showTypeHasElements = (showType: string) => {
    if (ShowTypes[props.event.sanctioning_club][showType][showType] !== undefined) {
      if (ShowTypes[props.event.sanctioning_club][showType][showType] !== undefined && 
          ShowTypes[props.event.sanctioning_club][showType][showType].length === 1 && 
          ShowTypes[props.event.sanctioning_club][showType][showType][0] === showType) {
        return false;
      }
    }

    return true;
  }
  

  const getBreed = useCallback((v: string, breeds?: IBreed[]) => {
      let hasBreed = breeds ? breeds.filter((b: IBreed) => { return b.breed.toLowerCase() === v.toLowerCase() }) : breedList.filter((b: IBreed) => { return b.breed.toLowerCase() === v.toLowerCase() });
  
      if (hasBreed.length > 0) {
        return hasBreed[0];
      }
  
      return undefined;
    }, [breedList]);

  const getShowTypeOptions = (sanctioningClub: string) => {
    let options:IOption[] = [];

    for (let i in ShowTypes[sanctioningClub]) {
      options.push({
        value: i,
        id: i
      });
    }

    return options;
  }

  const pushCheckListOption = (options: ICheck[], label: string, checked: boolean) => {
    options.push({ label, checked });
  }

  const getShowElementOptions = useCallback((newShowType: string) => {
    let options:ICheck[] = [];

    // show edit
    if (props.show && props.show.show_type === newShowType) {
      for (let showElement in ShowTypes[props.event.sanctioning_club][newShowType]) {
        // for licensed elements
        if (showElement !== Constants.dog_class_element.conformation_non_licensed) {
          let hasShowElement = props.show.show_elements.filter((se) => {
            return se.level ? se.level === showElement : se.show_element === showElement
          });

          pushCheckListOption(options, showElement, !!hasShowElement.length);
        // for non-licensed elements
        } else {
          for (let showLevel in ShowTypes[props.event.sanctioning_club][newShowType][showElement]) {
            const element = ShowTypes[props.event.sanctioning_club][newShowType][showElement];
            let hasShowLevel = props.show.show_elements.filter((se) => {
              return se.level ? se.level === element[showLevel] : se.show_element === element[showLevel];
            });

            pushCheckListOption(options, element[showLevel], !!hasShowLevel.length);
          }
        }
      }
    // show new
    } else {
      for (let showElement in ShowTypes[props.event.sanctioning_club][newShowType]) {
        if (showElement !== Constants.dog_class_element.conformation_non_licensed) {
          pushCheckListOption(options, showElement, true);
        } else {
          for (let showLevel in ShowTypes[props.event.sanctioning_club][newShowType][showElement]) {
            pushCheckListOption(options, ShowTypes[props.event.sanctioning_club][newShowType][showElement][showLevel], true);
          }
        }
      }
    }

    return options;
  }, [props.event.sanctioning_club, props.show]);

  // NOTE: AKC Fetch only feature; the show has no non-licensed elements
  const getElementJudgeOptions = useCallback((showType: string): ICheck[] => {
    const options: ICheck[] = [];

    // show edit
    if (props.show) {
      return props.show.show_elements.map((se) => {
        return {
          label: se.level ?? se.show_element,
          checked: !!se.use_judge_name,
        }
      }) as ICheck[];
    // show new
    } else {
      for (let showElement in ShowTypes[props.event.sanctioning_club][showType]) {
        pushCheckListOption(options, showElement, false);
      }
    }

    return options;
  }, [props.event.sanctioning_club, props.show]);

  const toggleCheck = (index: number) => {
    let _options:ICheck[] = [...showElements];

    _options[index].checked = !_options[index].checked;

    setShowElements(_options);
  }

  const toggleElementJudgeCheck = (index: number) => {
    const _options: ICheck[] = [...showElementJudges];

    _options[index].checked = !_options[index].checked;

    setShowElementJudges(_options);
  }

  const pShowType:any = helperService.usePrevious(showType);

  useEffect(() => {
    if (showType !== pShowType) {
      let _options:ICheck[] = getShowElementOptions(showType.id);
      setShowElements(_options);
      setArmNum(getArmBandNumStart(showType.id));

      if (Constants.show_type.fetch === showType.value) {
        let judge_options:ICheck[] = getElementJudgeOptions(showType.id);
        setShowElementJudges(judge_options);
      }
    }
  }, [showType, pShowType, getShowElementOptions, getElementJudgeOptions, getArmBandNumStart]);

  const pShowElements:any = helperService.usePrevious(showType);
  const pArmNum:any = helperService.usePrevious(armNum);
  const pBreedSearch:any = helperService.usePrevious(breedSearch);

  let { onChange } = props;

  useEffect(() => {
    if (JSON.stringify(pShowElements) !== JSON.stringify(showElements) || 
        pArmNum !== armNum || 
        pBreedSearch !== breedSearch) {
      if (showElements.length > 0) {
        onChange(clubName, showType, showElements, showElementJudges, parseInt(armNum), breedSearch);
      }
    }
  }, [onChange, clubName, showType, pShowElements, showElements, showElementJudges, armNum, pArmNum, breedSearch, pBreedSearch]);

  useEffect(() => {
    if (!loaded) {
      const hydrateShowTypeTab = () => {
        if (props.show) {
          setShowType({ value: props.show.show_type, id: props.show.show_type });
          setArmNum(props.show.arm_number_start?.toString() ?? getArmBandNumStart(props.show.show_type));
          setBreedSearch(props.show.specialty_breed ?? "");
        }
      }

      if (props.event) {
        let _options:IOption[] = getShowTypeOptions(props.event.sanctioning_club);

        setShowTypes(_options);

        if (!props.show) {
          setShowType(_options[0]);
        } else {
          hydrateShowTypeTab();
        }
      }

      setLoaded(true);
    }
  }, [loaded, props.event, props.show, getArmBandNumStart]);

  const handleBreedSearch = async () => {
    try {
      const breeds = await apiService.getBreeds();
      setBreedList(breeds);
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        toast.show({
          title: "Dog Breeds",
          content: "Unable to get dog breeds",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      }
    }
  };

  return (
    <div className={styles.ShowTypeTab}>
      <div className="stepTitle">Show Type and Elements</div>
      <p>What type of show are you running?</p>
      <Input required label="Club Name" defaultValue={clubName} type="text" onChange={(e) => { e.stopPropagation(); setClubName(e.target.value); }} />
      <Dropdown value={showType} onChange={(e: any, value: IOption) => {
        setShowType(value);
      }} label="Show Type" options={showTypes} placeholder="Choose a Show Type" />
      {props.event.sanctioning_club === Constants.sanctioning_club.AKC && (showType.value === Constants.show_type.conformation || showType.value === Constants.show_type.obedience || showType.value === Constants.show_type.rally_obedience) &&
          <SearchDown
          label="Specialty Breed"
          icon={star}
          id="specialtyBreed"
          type="text"
          onChange={(e) => {
            e.stopPropagation();
            setBreedSearch(e.target.value);
            setSelectedBreed(getBreed(e.target.value));
          }}
          onSelect={(value: string) => {
            setBreedSearch(value);
            setSelectedBreed(getBreed(value));
          }}
          onClear={() => {
            setBreedSearch("");
            setSelectedBreed(undefined);
          }}
          placeholder="If this is a specialty show, please select the breed."
          onSearch={handleBreedSearch}
          searchResults={breedList.filter((b: IBreed) => b.breed.toLowerCase() === breedSearch.toLowerCase()).length === 1 ? [] : breedList.filter((b: IBreed) => {
            if (b.breed.toLowerCase().includes(breedSearch.toLowerCase()) && b.breed.toLowerCase() !== breedSearch.toLowerCase()) {
              return b.breed;
            } else {
              return false;
            }}).map((b: IBreed) => b.breed)
          }
          defaultValue={breedSearch}
          showClear={true}
        />
      }
      {props.event.sanctioning_club === Constants.sanctioning_club.AKC && <Input className={styles.armBandStart} required onChange={(e) => { setArmNum(e.target.value); }} type="number" label="Armband Starting Number" placeholder="Enter an armband starting number." defaultValue={armNum} />}
      {props.event.sanctioning_club === Constants.sanctioning_club.AKC && <p className={styles.hint}>Armband starting numbers are based on show type. Changing the armband starting number will change the starting number for all shows of this show type.</p>}
      <>{showElements.length > 0 && showTypeHasElements(showType.id) && <div>
        <p>What elements are available at your show?</p>
        {showElements.map((check:ICheck, index:number) => {
          return(
            <div key={"showType" + index} className={styles.showTypeWrap}>
              <Checkbox
                key={"elem" + index}
                id={check.label}
                label={check.label}
                value={check.checked}
                onChange={(e) => {
                  toggleCheck(index);
                }}
              />
              {Constants.show_type.fetch === showType.value && <Checkbox
                id={check.label + "judge-name"}
                label="Use judge name in scoresheets"
                value={showElementJudges.find((elementJudge) => elementJudge.label === check.label)?.checked ?? false}
                onChange={(e) => {
                  toggleElementJudgeCheck(index);
                }}
              />}
            </div>
          );
        })}
      </div>}</>
    </div>
  );
};

export default ShowTypeTab;